import type { AxiosRequestConfig } from 'axios';
import type { ApiServiceTransport } from '@/services/ApiService';
import {
  GraphqlRequestError,
  graphqlOperationName,
  type IGraphqlErrorPayload,
} from '@/services/graphqlRequestError';
import { requestTolerantOfOlderServer } from '@/services/olderServer';
import { requestDesktopCloudApi } from './desktopRuntimeLazy';
import type { ICloudApiRequestInput } from './desktopRuntime';

type GraphqlError = IGraphqlErrorPayload;

interface GraphqlResponse<T> {
  data?: T;
  errors?: GraphqlError[];
}

export class DesktopCloudTransportError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The answer's body, so a GraphQL refusal's `errors` can still be read (I-1383). */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'DesktopCloudTransportError';
  }
}

export function createDesktopCloudApiTransport(
  desktopWorkAreaId: string,
  expectedBinding?: ICloudApiRequestInput['expectedBinding'],
): ApiServiceTransport {
  return {
    async request<T>(
      method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
      path: string,
      body?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<T> {
      if (body instanceof FormData) {
        throw new Error('Binary cloud uploads require the desktop attachment gateway.');
      }
      const response = await requestDesktopCloudApi<T>({
        desktopWorkAreaId,
        ...(expectedBinding ? { expectedBinding } : {}),
        method,
        path: appendQuery(path, config?.params),
        body,
      });
      if (response.status < 200 || response.status >= 300) {
        throw new DesktopCloudTransportError(
          errorMessage(response.body, response.status),
          response.status,
          response.body,
        );
      }
      return response.body as T;
    },
  };
}

export function createDesktopCloudGraphqlRequester(desktopWorkAreaId: string) {
  const transport = createDesktopCloudApiTransport(desktopWorkAreaId);
  async function requestOnce<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const response = await transport.request<GraphqlResponse<T>>('POST', '/graphql', {
      query,
      variables,
    });
    if (response.errors?.length) {
      throw new GraphqlRequestError(response.errors[0], graphqlOperationName(query));
    }
    if (!response.data) throw new Error('GraphQL response contained no data');
    return response.data;
  }
  return function request<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    // I-1383: a deployment behind the app is asked again without the fields it does not know.
    return requestTolerantOfOlderServer(query, (sent) => requestOnce<T>(sent, variables));
  };
}

function appendQuery(path: string, params: AxiosRequestConfig['params']): string {
  if (!params || typeof params !== 'object') return path;
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) value.forEach((entry) => query.append(key, String(entry)));
    else query.set(key, String(value));
  }
  const encoded = query.toString();
  if (!encoded) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${encoded}`;
}

function errorMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const value = body as Record<string, unknown>;
    if (Array.isArray(value.message)) return value.message.map(String).join(' ');
    if (typeof value.message === 'string' && value.message.trim()) return value.message;
    if (typeof value.error === 'string' && value.error.trim()) return value.error;
  }
  return `Cloud request failed with HTTP ${status}.`;
}
